/**
 * The airship's look numbers that are not the scene record's (ADR 0033; the
 * renderer's rule: presentation numbers live in a constants module, the
 * arithmetic of a lifted builder stays in its code). Each is brad-airship's,
 * named after where it was set.
 */
export const shipLook = {
  /** The sun's shadow (`airship-world.js`): camera near and far, biases (the half extents are the record's `lighting.sun`). */
  sun: { near: 1, far: 100, bias: -0.0005, normalBias: 0.04, distance: 45 },
  /** The deck's plain-tint colours (`DECK_PALETTE`); `deckAlternate` is the floor × 0.965. */
  deckPalette: { floor: '#ad9064', timber: '#594330', trim: '#343634', cream: '#d1c39e', blue: '#456f82' } as Readonly<Record<string, string>>,
  deckAlternate: 0.965,
  /** Below deck (`configureAirshipLighting`): the cream walls, the hatchery's lamp glow, its copper. */
  interior: { cream: '#aaa18b', hatcheryLamp: 1.9, copperRoughness: 0.29, copperMetalness: 0.28 },
  /** The lamp bake's sources (`airship-world.js`): a lit window's strength, a lamp inside a deckhouse's, and how close two may be before they count once. */
  lampSources: { window: 0.7, inRoom: 0.45, merge: 0.35, windowTop: 0.3, windowBottom: 0.2, roomMargin: 0.3 },
  /** The roof reveal (`airship-reveal.js`): the margin round a room that keeps its roof open in a doorway, and the fade's rate per second. */
  reveal: { margin: 0.6, rate: 7, snap: 0.004 },
  /** The lens (`airship-focus.js` `applyAirshipGrade` at High): strength, and its factor on a portrait screen (aspect under `portraitBelow`). */
  lens: { strength: 1.12, portraitFactor: 0.6, portraitBelow: 0.8, shadowLift: [0.002, 0.003, 0.005] as const, shadowChroma: 0.003, focusHeight: 0.8 },
  /** The sun glare's fade off screen and behind cloud (`applyAirshipGrade`). */
  glare: { reach: 2.2, bias: 1.1, cloud: 0.8 },
  /** Contact occlusion (`ContactOcclusionPass`, intensity 0.75 on the ship) and bloom (`UnrealBloomPass(…, 0.65, 1.15)` at half size). */
  occlusion: { samples: 12, radius: 0.85, thickness: 0.55, distanceExponent: 1.6, intensity: 0.75 },
  bloom: { radius: 0.65, threshold: 1.15, scale: 0.5 },
  /** The sky's cloud drift (`installTimeOfDay` `update`): base speed and its wind factor, the flight's speed astern. */
  drift: { base: 1.6, wind: 2.6, flight: 16 },
  /** The motes' drift (`createMotes` as called): base and its wind factor. */
  moteDrift: { base: 0.6 },
  /** The deck's cloud-dimmed sun (`0.9 + 0.1 × behind`). */
  cloudSun: { floor: 0.9, share: 0.1 },
  /** The time of day's idle gusts: two slow incommensurate swells (`timeState.gust`) and the flight's extra wind. */
  gust: { mean: 0.5, a: 0.35, aRate: 0.23, b: 0.25, bRate: 0.61, bPhase: 1.3, low: 0.75, high: 0.5, flight: 0.8 },
  /** The ship's frame for the overview and the deck's radiance: the jetty's far corners added to the built bounds (`airship-world.js`). */
  frameCorners: [
    [6.8, 0, -5],
    [6.8, 0, 6],
  ] as const,
  /** The jetty's cantilever braces (`airship-world.js`, Higuera's coordinates). */
  braces: { rails: { x: 18.5, y: -0.32, zs: [-4.4, 5.4] as const, length: 13.4, height: 0.5, width: 0.32 } },
  /** The off tier's sky: a vertical gradient from the horizon to the zenith (no sky shader beyond it). */
  offSky: { horizonAt: 0.45 },
  /** The wind at Off: still (no wind motion at Off, ADR 0033). */
  wind: { offStrength: 0 },
  /**
   * The characters aboard (`airship-main.js`): Brad's card's self-light (emissive 0.32, the rest from the lamp bake on
   * deck and the radiance below) and his contact shadow (a black blob at 0.34).
   */
  sprite: { selfLight: 0.32, contact: { color: '#000000', opacity: 0.34 } },
} as const;
